import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileText, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Report {
  slug: string;
  title: string;
  abstract: string;
  sector: string | null;
  county_scope: string | null;
  quarter_label: string | null;
  page_count: number | null;
  published_at: string | null;
}

const SECTOR_ORDER = ["Health & Wellness", "Community Wellness", "Education", "Economic Development", "Cross-sector"];

function groupBySector(reports: Report[]): [string, Report[]][] {
  const groups = new Map<string, Report[]>();
  for (const r of reports) {
    const key = r.sector?.trim() || "Cross-sector";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  const known = SECTOR_ORDER.filter((s) => groups.has(s)).map((s) => [s, groups.get(s)!] as [string, Report[]]);
  const rest = [...groups.entries()].filter(([k]) => !SECTOR_ORDER.includes(k));
  return [...known, ...rest];
}

export default function IntelligenceLibrary() {
  const { data: reports, isLoading } = useQuery({
    queryKey: ["intelligence_reports_library"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intelligence_reports")
        .select("slug, title, abstract, sector, county_scope, quarter_label, page_count, published_at")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Report[];
    },
  });

  const grouped = useMemo(() => groupBySector(reports ?? []), [reports]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Intelligence Library — The Emerging Market</title>
        <meta
          name="description"
          content="Quarterly and sector briefs on Alabama's emerging economic ecosystem — capital flow, legislation, funders, and community pipeline. Free preview; Member Pro unlocks the full PDF."
        />
        <link rel="canonical" href="https://emrkt.co/intelligence" />
        <meta property="og:title" content="Intelligence Library — The Emerging Market" />
        <meta property="og:url" content="https://emrkt.co/intelligence" />
        <meta property="og:type" content="website" />
      </Helmet>
      <SiteHeader />
      <main className="container max-w-6xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-10 max-w-3xl">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" /> Intelligence
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">
            Intelligence Library
          </h1>
          <p className="mt-4 text-muted-foreground text-lg">
            Quarterly briefings and sector reports on Alabama's emerging
            economic ecosystem — capital flow, legislation, funders, and the
            community pipeline. Every brief is grounded in the same open{" "}
            <Link to="/methodology" className="text-primary underline">methodology</Link>.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-muted px-2 py-1">Free abstract + TOC</span>
            <span className="rounded-full bg-muted px-2 py-1">Member Pro: full PDF</span>
            <span className="rounded-full bg-muted px-2 py-1">Intelligence tier: raw dataset</span>
          </div>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-40 rounded-2xl border border-border bg-card animate-pulse" />
            ))}
          </div>
        ) : (reports?.length ?? 0) === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h2 className="font-display text-xl font-semibold">First briefing dropping soon</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Follow along on the{" "}
              <Link to="/for-funders" className="text-primary underline">funders landing page</Link>{" "}
              to be notified.
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {grouped.map(([sector, list]) => (
              <section key={sector}>
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-display text-xl font-semibold text-foreground">{sector}</h2>
                  <span className="text-xs text-muted-foreground">{list.length} brief{list.length === 1 ? "" : "s"}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {list.map((r) => (
                    <article
                      key={r.slug}
                      className="rounded-2xl border border-border bg-card p-6 flex flex-col"
                    >
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                        {r.quarter_label && (
                          <span className="rounded-full bg-muted px-2 py-1">{r.quarter_label}</span>
                        )}
                        {r.county_scope && (
                          <span className="rounded-full bg-muted px-2 py-1">{r.county_scope}</span>
                        )}
                        {r.page_count && (
                          <span className="rounded-full bg-muted px-2 py-1">{r.page_count} pp</span>
                        )}
                      </div>
                      <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                        <Link to={`/intelligence/reports/${r.slug}`} className="hover:text-primary transition-colors">
                          {r.title}
                        </Link>
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-4 flex-1">{r.abstract}</p>
                      <div className="mt-4">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/intelligence/reports/${r.slug}`}>
                            Read brief <ArrowRight className="h-3 w-3 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 text-sm">
          <h2 className="font-display text-xl font-semibold text-foreground mb-2">
            Institutional licensing
          </h2>
          <p className="text-muted-foreground">
            Intelligence-tier subscribers receive full historical archive,
            underlying datasets, and a named point of contact for custom
            briefings. See{" "}
            <Link to="/for-funders" className="text-primary underline">/for-funders</Link>{" "}
            or{" "}
            <Link to="/for-agencies" className="text-primary underline">/for-agencies</Link>{" "}
            to request a demo.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}